import { Stack, IconButton, useMediaQuery } from '@mui/material'
import { styled } from '@mui/material/styles'
import { PlayIcon, PauseIcon } from '@weblite/icons'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { pauseAudio, playAudio, useAudioState } from '@weblite/audio'
import { SendIcon } from '$common/icons/send'
import { SendFieldTrashIcon } from '$common/icons/send-field-trash'
import { SEND_FIELD_DEFAULT_HEIGHT } from '$comp/_app/components/chat/components/conf'
import { useSeek } from '$common/react-hooks/message'
import { Surfer } from '$comp/_app/components/chat/components/body/components/messageList/messages/message-bodies/voice/surfer'
import { SendFieldIconButton } from '../../icon-button'
import { durationLabelRenderer } from '../components/durationLabelRenderer'
import { RecordedVoice } from '$comp/_app/components/chat/components/footer/components/send-field/components/voice-recorder/types'
import { interpolateArray } from '$func/math'

interface Props {
  onReset: () => unknown
  onSend: () => unknown
  recorderVoice?: RecordedVoice
  showSendButton?: boolean
}

const StyledIconButton = styled(SendFieldIconButton)`
  /* @noflip */
  margin-left: auto;
`
export function VoicePreview({
  onReset,
  onSend,
  recorderVoice,
  showSendButton = true,
}: Props) {
  const [blobUrl, setBlobUrl] = useState<string>()

  useEffect(() => {
    if (recorderVoice) {
      const url = URL.createObjectURL(recorderVoice.blob)
      setBlobUrl(url)
      return () => URL.revokeObjectURL(url)
    }
    setBlobUrl(undefined)
  }, [recorderVoice])

  const handleReset = useCallback(() => {
    pauseAudio(blobUrl)
    onReset()
  }, [blobUrl, onReset])

  const handleSend = useCallback(() => {
    onSend()
    pauseAudio(blobUrl)
  }, [blobUrl, onSend])

  useEffect(
    () => () => {
      pauseAudio(blobUrl)
    },
    [blobUrl],
  )

  return (
    <Stack
      direction="row"
      justifyContent="space-between"
      gap="8px"
      width="100%"
      alignItems="center"
      padding="2px 8px"
      boxSizing="border-box"
      height={`${SEND_FIELD_DEFAULT_HEIGHT}px`}
      sx={{
        backgroundColor: 'white',
        borderTop: '1px solid #0000001A',
      }}
    >
      {showSendButton && (
        <StyledIconButton onClick={handleSend}>
          <SendIcon />
        </StyledIconButton>
      )}

      {blobUrl && recorderVoice?.wave && (
        <VoicePreviewPlayer
          duration={recorderVoice.duration}
          blobURL={blobUrl}
          wave={recorderVoice.wave}
        />
      )}

      <SendFieldIconButton onClick={handleReset}>
        <SendFieldTrashIcon />
      </SendFieldIconButton>
    </Stack>
  )
}

function VoicePreviewPlayer({
  blobURL,
  wave,
  duration,
}: {
  blobURL: string
  wave: number[]
  duration: number
}) {
  const playState = useAudioState(blobURL)
  const containerRef = useRef<HTMLDivElement>(null)
  const seekContainerRef = useRef<HTMLDivElement>(null)

  const isTablet = useMediaQuery('(min-width:768px)')

  const playing = playState === 'PLAYING'

  const seekValue = useSeek({
    src: blobURL,
    canSeek: true,
    containerRef,
    seekContainerRef,
  })

  const togglePlay = useCallback(() => {
    if (playing) pauseAudio(blobURL)
    else {
      playAudio(blobURL, duration / 1000, { type: 'VOICE' })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playing, blobURL, duration])

  const interpolatedWave = useMemo(() => {
    if (isTablet) return interpolateArray(wave, 54)
    return wave
  }, [wave, isTablet])

  return (
    <PlayerRoot ref={containerRef}>
      {durationLabelRenderer(duration, 'subtitle3', 'neutral700', false)}

      <PlayerWaveContainer>
        <Surfer
          ref={seekContainerRef}
          wave={interpolatedWave}
          seekValue={seekValue}
          sx={{ mb: 0 }}
          variant="preview"
        />
      </PlayerWaveContainer>
      <IconButton onClick={togglePlay} sx={{ width: '40px', height: '40px' }}>
        {!playing ? (
          <PlayIcon
            sx={{ width: '20px', height: '20px', color: 'neutral700' }}
          />
        ) : (
          <PauseIcon
            sx={{ width: '20px', height: '20px', color: 'neutral700' }}
          />
        )}
      </IconButton>
    </PlayerRoot>
  )
}

const PlayerRoot = styled('div')`
  height: 40px;
  display: flex;
  align-items: center;
  background-color: ${({ theme }) => theme.palette.neutral100};
  border-radius: 20px;
  flex-grow: 1;
  padding-right: 12px;
`

const PlayerWaveContainer = styled('div')`
  flex-grow: 1;
  margin-right: 8px;
`
